/*
 * Flashburn product media modal
 * Version 2.3
 *
 * This implementation intentionally does not extend Dawn's ModalDialog.
 * Dawn's inherited modal click handling was interpreting the Flashburn
 * Previous and Next controls as close actions.
 */

if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends HTMLElement {
      constructor() {
        super();

        this.currentIndex = 0;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.opener = null;

        this.onModalClick = this.onModalClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
      }

      connectedCallback() {
        this.dialog = this.querySelector('[role="dialog"]');
        this.gallery = this.querySelector('[data-flashburn-gallery]');
        this.viewport = this.querySelector('[data-gallery-viewport]');
        this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
        this.closeButton = this.querySelector('[data-gallery-close]');
        this.previousButton = this.querySelector('[data-gallery-prev]');
        this.nextButton = this.querySelector('[data-gallery-next]');
        this.currentElement = this.querySelector('[data-gallery-current]');
        this.totalElement = this.querySelector('[data-gallery-total]');
        this.dotsContainer = this.querySelector('[data-gallery-dots]');

        /*
         * One click handler owns every modal action. Nothing inside the
         * gallery can close the modal unless it is the dedicated close button.
         */
        this.addEventListener('click', this.onModalClick);

        this.viewport?.addEventListener('touchstart', this.onTouchStart, {
          passive: true,
        });
        this.viewport?.addEventListener('touchend', this.onTouchEnd, {
          passive: true,
        });

        this.buildDots();
        this.updateTotal();
        this.updateControls();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onModalClick);
        document.removeEventListener('keydown', this.onKeydown);
        this.viewport?.removeEventListener('touchstart', this.onTouchStart);
        this.viewport?.removeEventListener('touchend', this.onTouchEnd);
      }

      show(opener) {
        this.opener = opener || document.activeElement;

        const pageGallery = opener?.closest('media-gallery');
        const activeHero = pageGallery?.querySelector('[data-media-id].is-active');
        const activeHeroMediaId = activeHero?.dataset.mediaId;
        const openerMediaId =
          opener?.getAttribute('data-media-id') ||
          opener?.closest('[data-media-id]')?.getAttribute('data-media-id');

        const requestedIndex = this.findSlideIndex(
          activeHeroMediaId,
          openerMediaId
        );

        this.showSlide(requestedIndex >= 0 ? requestedIndex : 0, false);

        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKeydown);

        requestAnimationFrame(() => {
          this.dialog?.focus();
        });
      }

      hide() {
        if (!this.hasAttribute('open')) return;

        window.pauseAllMedia?.();

        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKeydown);

        if (this.opener && typeof this.opener.focus === 'function') {
          this.opener.focus();
        }
      }

      onModalClick(event) {
        const closeControl = event.target.closest('[data-gallery-close]');
        const previousControl = event.target.closest('[data-gallery-prev]');
        const nextControl = event.target.closest('[data-gallery-next]');
        const dotControl = event.target.closest('[data-gallery-dot]');

        if (previousControl) {
          event.preventDefault();
          event.stopPropagation();
          this.showPrevious();
          return;
        }

        if (nextControl) {
          event.preventDefault();
          event.stopPropagation();
          this.showNext();
          return;
        }

        if (dotControl) {
          event.preventDefault();
          event.stopPropagation();

          const dotIndex = Number.parseInt(
            dotControl.dataset.galleryIndex,
            10
          );

          if (Number.isInteger(dotIndex)) {
            this.showSlide(dotIndex);
          }

          return;
        }

        if (closeControl) {
          event.preventDefault();
          event.stopPropagation();
          this.hide();
          return;
        }

        /*
         * Backdrop clicks close the modal. Clicks on the dialog, gallery,
         * images, controls and footer do not.
         */
        if (event.target === this) {
          this.hide();
        }
      }

      findSlideIndex(...mediaIds) {
        const validIds = mediaIds.filter(Boolean);

        for (const mediaId of validIds) {
          const index = this.slides.findIndex((slide) =>
            this.mediaIdsMatch(slide.dataset.mediaId, mediaId)
          );

          if (index >= 0) return index;
        }

        return -1;
      }

      mediaIdsMatch(slideMediaId, requestedMediaId) {
        if (!slideMediaId || !requestedMediaId) return false;
        if (slideMediaId === requestedMediaId) return true;

        const slideId = String(slideMediaId).split('-').pop();
        const requestedId = String(requestedMediaId).split('-').pop();

        return slideId === requestedId;
      }

      showSlide(index, announce = true) {
        if (!this.slides.length) return;

        const normalizedIndex =
          (index + this.slides.length) % this.slides.length;

        window.pauseAllMedia?.();

        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === normalizedIndex;

          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          slide.hidden = !isActive;
        });

        this.currentIndex = normalizedIndex;
        this.updateControls();
        this.loadActiveMedia();

        if (announce && this.currentElement) {
          this.currentElement.setAttribute('aria-live', 'polite');
        }
      }

      showPrevious() {
        this.showSlide(this.currentIndex - 1);
      }

      showNext() {
        this.showSlide(this.currentIndex + 1);
      }

      updateControls() {
        if (this.currentElement) {
          this.currentElement.textContent = String(this.currentIndex + 1);
        }

        if (this.totalElement) {
          this.totalElement.textContent = String(this.slides.length);
        }

        this.querySelectorAll('[data-gallery-dot]').forEach((dot, index) => {
          const isActive = index === this.currentIndex;

          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        const hideNavigation = this.slides.length <= 1;

        if (this.previousButton) this.previousButton.hidden = hideNavigation;
        if (this.nextButton) this.nextButton.hidden = hideNavigation;
        if (this.dotsContainer) this.dotsContainer.hidden = hideNavigation;
      }

      updateTotal() {
        if (this.totalElement) {
          this.totalElement.textContent = String(this.slides.length);
        }
      }

      buildDots() {
        if (!this.dotsContainer) return;

        this.dotsContainer.innerHTML = '';

        this.slides.forEach((slide, index) => {
          const dot = document.createElement('button');

          dot.type = 'button';
          dot.className = 'flashburn-product-gallery__dot';
          dot.setAttribute('data-gallery-dot', '');
          dot.dataset.galleryIndex = String(index);
          dot.setAttribute('aria-label', `Show product image ${index + 1}`);

          this.dotsContainer.appendChild(dot);
        });
      }

      loadActiveMedia() {
        const activeSlide = this.slides[this.currentIndex];
        if (!activeSlide) return;

        const deferredMedia = activeSlide.querySelector('deferred-media');

        if (
          deferredMedia &&
          typeof deferredMedia.loadContent === 'function'
        ) {
          deferredMedia.loadContent(false);
        }
      }

      onKeydown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.hide();
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showPrevious();
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showNext();
        }
      }

      onTouchStart(event) {
        this.touchStartX = event.changedTouches[0]?.screenX || 0;
      }

      onTouchEnd(event) {
        this.touchEndX = event.changedTouches[0]?.screenX || 0;

        const distance = this.touchEndX - this.touchStartX;

        if (Math.abs(distance) < 50) return;

        if (distance > 0) {
          this.showPrevious();
        } else {
          this.showNext();
        }
      }
    }
  );
}